import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
    Box,
    Text,
    useToast,
    VStack,
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    Spinner,
    Image,
    Heading,
    Container,
    Center,
    useBreakpointValue, Button, Link
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import logo from './High res brand face logo.png'; // You'll need to add your logo file here
import moment from "moment";

// Color theme constants
const COLORS = {
    isabelline: "#F4EEEA",    // Background, card backgrounds
    maastrichtBlue: "#0E1735", // Headings, important text
    shadowBlue: "#7481AF",    // Secondary elements, borders, labels
    mandarin: "#F17946",      // CTAs, highlights, errors
    shinyShamrock: "#56A27F"  // Success states, secondary actions
};

export const SuccessRegistration = () => {
    const { registrationID } = useParams();
    const [registrationData, setRegistrationData] = useState(null);
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [clickCount, setClickCount] = useState(0);
    const [showBackdoor, setShowBackdoor] = useState(false);
    
    // Responsive values
    const logoSize = useBreakpointValue({ base: "80px", md: "120px" });
    const headingSize = useBreakpointValue({ base: "lg", md: "lg" });
    const mainFontSize = useBreakpointValue({ base: "md", md: "lg" });
    const detailsFontSize = useBreakpointValue({ base: "sm", md: "md" });
    const cardPadding = useBreakpointValue({ base: 10, md: 10 });
    const containerMaxW = useBreakpointValue({ base: "100%", md: "container.md" });

    // Calculate payment eligibility
    const getPaymentEligibility = () => {
        if (!registrationData?.start_date) return null;
        
        const startDate = moment(registrationData.start_date);
        const today = moment();
        const daysUntilEligible = startDate.diff(today, 'days');
        
        // Backdoor override for testing
        if (showBackdoor) {
            return {
                canPayNow: true,
                daysLeft: 0,
                daysUntilCourse: daysUntilEligible,
                skillsFutureAvailableDate: startDate.subtract(60, 'days')
            };
        }
        
        return {
            canPayNow: daysUntilEligible <= 60,
            daysLeft: daysUntilEligible > 60 ? daysUntilEligible - 60 : 0,
            daysUntilCourse: daysUntilEligible,
            skillsFutureAvailableDate: startDate.subtract(60, 'days')
        };
    };

    // Handle title click for backdoor
    const handleTitleClick = () => {
        const newCount = clickCount + 1;
        setClickCount(newCount);
        
        if (newCount === 5) {
            setShowBackdoor(!showBackdoor);
            setClickCount(0);
            toast({
                title: showBackdoor ? "Backdoor Deactivated" : "Backdoor Activated",
                description: showBackdoor ? "Showing normal SkillsFuture eligibility" : "Showing SkillsFuture as available",
                status: "info",
                duration: 2000,
                isClosable: true,
                position: "top",
            });
        }
    };

    useEffect(() => {
        const fetchRegistration = async () => {
          setLoading(true);
          try {
            const { data, error } = await supabase
              .from("full_registration_details_with_enrollment")
              .select(
                "id, participant_name, participant_email, name, start_date, end_date, price, course_run_id, registration_type, registration_stage, billing_type, birth_age, nationality,course_dates"
              )
              .gte("start_date", new Date().toLocaleDateString('fr-CA'))
              .eq("id", registrationID)
              .single();
    
            if (error) throw error;
    
            setRegistrationData(data);
          } catch (error) {
            console.error("Error fetching registration:", error);
            toast({
              title: "Error",
              description: "Could not load registration details.",
              status: "error",
              isClosable: true,
              position: "top",
            });
          } finally {
            setLoading(false);
          }
        };
    
        fetchRegistration();
      }, [registrationID, toast]);

    return (
        <Box 
            w="100%"
            bg={COLORS.isabelline}
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={{ base: 8, md: 12 }}
            px={{ base: 4, md: 8 }}
        >
            {loading ? (
                <Center h="60vh">
                    <Spinner 
                        size="xl" 
                        thickness="4px" 
                        speed="0.65s" 
                        color={COLORS.mandarin}
                    />
                </Center>
            ) : (
                <Box maxW={containerMaxW}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <Box 
                            bg="white" 
                            p={cardPadding}
                            borderRadius="lg" 
                            boxShadow="md"
                        >
                            <VStack spacing={{base: 4, md: 8}}>
                                <motion.div
                                    initial={{y: -50}}
                                    animate={{y: 0}}
                                    transition={{delay: 0.2, duration: 0.8, ease: "easeOut"}}
                                >
                                    <Heading
                                        size={"sm"}
                                        color={COLORS.maastrichtBlue}
                                        borderBottom="2px solid"
                                        borderColor={COLORS.mandarin}
                                        pb={2}
                                        onClick={handleTitleClick}
                                        userSelect="none"
                                    >
                                        Registration received
                                    </Heading>
                                </motion.div>

                                <motion.div
                                    initial={{opacity: 0}}
                                    animate={{opacity: 1}}
                                    transition={{delay: 0.5, duration: 1, ease: "easeOut"}}
                                >
                                    <Text
                                        fontSize={"md"}
                                        color={COLORS.shadowBlue}
                                        lineHeight="1.6"
                                    >
                                        {registrationData.registration_type === "Company-Sponsored" ? (
                                            <>
                                                Please give us some time to review your application. <br></br>You will
                                                receive a response at{" "}
                                                <Text
                                                    as="span"
                                                    color={COLORS.mandarin}
                                                    fontWeight="600"
                                                >
                                                    {registrationData.participant_email}
                                                </Text>
                                                {" "}soon!
                                            </>
                                        ) : (
                                            <VStack spacing={4}>
                                                <Text>You will receive an enrollment pending email with the student contract link sent to {<Text
                                                    as={"span"} fontWeight={"600"}
                                                    color={COLORS.mandarin}>{registrationData.participant_email}</Text>} soon. Please verify that you have received this email before proceeding.
                                                    To confirm your enrollment, please proceed to review the student contract and make payment by
                                                    clicking on the button below.</Text>
                                                <Text
                                                    fontSize={"sm"}
                                                    color={COLORS.maastrichtBlue}
                                                    fontWeight="700"
                                                >
                                                    Note: Skillsfuture payment can only be used less than 60 days before the course
                                                    start date.
                                                </Text>
                                                
                                                {/* Payment Eligibility Message */}
                                                {(() => {
                                                    const eligibility = getPaymentEligibility();
                                                    if (!eligibility) return null;
                                                    
                                                    if (eligibility.canPayNow) {
                                                        return (
                                                            <Text
                                                                fontSize={"sm"}
                                                                color={COLORS.shinyShamrock}
                                                                fontWeight="600"
                                                            >
                                                                ✅ SkillsFuture payment is now available! (Course starts in {eligibility.daysUntilCourse} days)
                                                            </Text>
                                                        );
                                                    } else {
                                                        return (
                                                            <Text
                                                                fontSize={"sm"}
                                                                color={COLORS.mandarin}
                                                                fontWeight="600"
                                                            >
                                                                ⏳ SkillsFuture payment will be available in {eligibility.daysLeft} days on {eligibility.skillsFutureAvailableDate.format("DD MMM YYYY (ddd)")}
                                                            </Text>
                                                        );
                                                    }
                                                })()}
                                                
                                                <Button 
                                                    size={"lg"} 
                                                    bg={COLORS.mandarin}
                                                    color="white"
                                                    _hover={{
                                                        bg: "#E06A3A",
                                                        transform: "scale(1.02)",
                                                        boxShadow: "0 10px 30px rgba(241, 121, 70, 0.4)",
                                                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                                    }}
                                                    _active={{
                                                        bg: "#D45A2A",
                                                        transform: "scale(0.98)",
                                                        boxShadow: "0 5px 20px rgba(241, 121, 70, 0.3)"
                                                    }}
                                                    _focus={{
                                                        boxShadow: "0 0 0 3px rgba(241, 121, 70, 0.3)"
                                                    }}
                                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                                    fontWeight="600"
                                                    letterSpacing="0.5px"
                                                    onClick={() => window.location.href = `/enrollment/${registrationID}`}
                                                    _textDecoration="none"
                                                    _outline="none"
                                                >
                                                    {getPaymentEligibility()?.canPayNow 
                                                        ? "Proceed to payment" 
                                                        : "Proceed to payment without SkillsFuture credit"
                                                    }
                                                </Button>
                                                
                                                {/* Reminder message for when SkillsFuture is not available */}
                                                {!getPaymentEligibility()?.canPayNow && (
                                                    <Text
                                                        fontSize={"sm"}
                                                        color={COLORS.shadowBlue}
                                                        textAlign="center"
                                                        fontStyle="italic"
                                                    >
                                                        We will send out a reminder email once SkillsFuture credit payment is available. 
                                                        You may close the page now if you are intending to pay via SkillsFuture credit.
                                                    </Text>
                                                )}
                                            </VStack>
                                        )}
                                    </Text>
                                </motion.div>

                                {/* Course Details Section */}
                                <motion.div
                                    initial={{opacity: 0, y: 20}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{delay: 0.7, duration: 0.8}}
                                    style={{width: '100%'}}
                                >
                                    <Box
                                        p={{base: 3, md: 6}}
                                        bg={COLORS.isabelline}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={COLORS.shadowBlue}
                                    >
                                        <Text
                                            fontSize={mainFontSize}
                                            fontWeight="600"
                                            color={COLORS.maastrichtBlue}
                                            mb={{base: 2, md: 4}}
                                        >
                                            Course Details
                                        </Text>
                                        <VStack spacing={{base: 2, md: 3}} align="start">
                                            <Text color={COLORS.shadowBlue} fontSize={detailsFontSize}>
                                                <strong>Course:</strong> {registrationData.name}
                                            </Text>
                                            <Text color={COLORS.shadowBlue} fontSize={detailsFontSize}>
                                                <strong>Start
                                                    Date:</strong> {moment(registrationData.start_date).format("DD MMM YYYY")} ({moment(registrationData.start_date).format("ddd")})
                                            </Text>
                                            <Text color={COLORS.shadowBlue} fontSize={detailsFontSize}>
                                                <strong>End
                                                    Date:</strong> {moment(registrationData.end_date).format("DD MMM YYYY")} ({moment(registrationData.end_date).format("ddd")})
                                            </Text>
                                            <Text color={COLORS.shadowBlue} fontSize={detailsFontSize}>
                                                <strong>Registration Type:</strong> {registrationData.registration_type}
                                            </Text>
                                        </VStack>
                                    </Box>
                                </motion.div>
                            </VStack>
                        </Box>
                    </motion.div>
                </Box>
            )}
        </Box>
    );
};

export default SuccessRegistration;